import React from 'react';
import { Stack, Text } from '@99designs/emu';
import { FormattedMessage } from '@99designs/frontend-components/i18n';
import { HeadingWithBody, TileSelect } from '@99designs/spa-common';
import { Answer } from '../../components/Step/Answer';
import type { StepComponentProps, StepHeadingProps } from '../../components/Onboarder/Config';
import type { Data } from '../config';

export type PrimaryGoalValue =
    | 'promote-business'
    | 'sell-things-online'
    | 'share-ideas'
    | 'showcase-work'
    | 'promote-product-or-service'
    | 'get-online';

export function PrimaryGoalStepInputs({ data, onChange }: StepComponentProps<Data>) {
    return (
        <Stack space="2bu">
            <TileSelect<PrimaryGoalValue>
                name="primaryGoal"
                value={data.primaryGoal}
                options={[
                    {
                        label: (
                            <HeadingWithBody
                                heading={__('Promote my business')}
                                body={__('Advertise or inform')}
                            />
                        ),
                        value: 'promote-business',
                    },
                    {
                        label: (
                            <HeadingWithBody
                                heading={__('Sell things online')}
                                body={__('E-commerce')}
                            />
                        ),
                        value: 'sell-things-online',
                    },
                    {
                        label: <HeadingWithBody heading={__('Share ideas')} body={__('Blog')} />,
                        value: 'share-ideas',
                    },

                    {
                        label: (
                            <HeadingWithBody heading={__('Showcase work')} body={__('Portfolio')} />
                        ),
                        value: 'showcase-work',
                    },
                    {
                        label: (
                            <HeadingWithBody
                                heading={__('Promote a product or service')}
                                body={__('Marketing page')}
                            />
                        ),
                        value: 'promote-product-or-service',
                    },
                    {
                        label: <HeadingWithBody heading={__('Get online')} body={__('Other')} />,
                        value: 'get-online',
                    },
                ]}
                onChange={(value, name) => {
                    const newData: Data = {
                        ...data,
                        [name]: value,
                    };
                    onChange(newData);
                }}
            />
            <Text color="grey700" size="size2">
                <strong>{__('Please note')}:</strong>{' '}
                {__('Your selection helps us ensure that we can meet your primary website need.')}
            </Text>
        </Stack>
    );
}

export const answerCopy = (): { [key: string]: string } => ({
    'promote-business': __('promote my business'),
    'sell-things-online': __('sell things online'),
    'share-ideas': __('share ideas'),
    'showcase-work': __('showcase work'),
    'promote-product-or-service': __('promote a product or service'),
    'get-online': __('get online'),
});

export const questionCopy = () => __('The primary goal of this website is to {answer}');

export function PrimaryGoalStepHeading({ data }: StepHeadingProps<Data>) {
    const answer = data.primaryGoal ? answerCopy()[data.primaryGoal] : '';

    return (
        <FormattedMessage message={questionCopy()} values={{ answer: <Answer text={answer} /> }} />
    );
}
